/* Feature spots on the product fold photo (round 6, 30 Sep 2026; round 7: fine drawn arrows instead of rings and
   pills). Data: snippets/hod-fold-spots.liquid.
   - Landscape screens only (the fold shows the 3:2 landscape file): a small caps label with a thin curved arrow whose
     tip touches the part it names. The arrow draws itself in, one feature after another.
   - Placement follows object-fit: cover + the photo's object-position, so a spot stays on the product at any size.
   - A spot whose label would run into the buy box, the header, a screen edge or another label is dropped; a feature
     with a visible spot leaves the text list visually (screen readers still read the list; the spots are aria-hidden).
   - Fixer C (1 Oct): the list is hidden from the first paint by snippets/hod-fold-critical-css.liquid on the same
     media test, so nothing moves when the arrows arrive; .hod-fold--spots-none restores it when none can be drawn.
   - Re-drawn on resize and whenever the colour swap changes data-hod-fold-key.
   - 2 Oct (Jono: "even if we just do two features on mobile"): landscape arrows from 750px (was 900px), and a portrait
     mode for phones and portrait tablets. It reads the '-p' entries (the 2:3 portrait file), which name features 2 and 3
     only. Phones already show only feature 1 as text (critical CSS), so the arrows add the other two with no layout
     shift; the class is .has-spots-p, so the text line stays. */
(function () {
  'use strict';
  var fold = document.querySelector('[data-hod-fold][data-hod-fold-kind="product"]');
  if (!fold) return;
  var dataEl = (fold.parentElement || document).querySelector('script[data-hod-fold-spots]');
  if (!dataEl) return;
  var data; try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
  var landscape = window.matchMedia('not all and (max-aspect-ratio: 3/4)');
  var wide = window.matchMedia('(min-width: 750px)');
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var layer = document.createElement('div');
  layer.className = 'hod-spots';
  layer.setAttribute('aria-hidden', 'true');
  var content = fold.querySelector('.hod-fold__content');
  fold.insertBefore(layer, content);
  // Tip at the svg's bottom-left corner (0, 44), tail at the top-right where the label sits; mirrored for 'l'.
  var ARROW = '<svg class="hod-spot__arrow" viewBox="0 0 64 44" width="64" height="44" aria-hidden="true" focusable="false">' +
    '<path class="hod-spot__stem" pathLength="1" d="M61 3C40 4 14 13 5 39"/>' +
    '<path class="hod-spot__head" pathLength="1" d="M11 34.5L5 40L3 31.8"/></svg>';
  // Round 10: 'd' drops onto the part from a label centred above it (tip at the svg's bottom centre).
  var ARROW_D = '<svg class="hod-spot__arrow hod-spot__arrow--d" viewBox="0 0 24 56" width="24" height="56" aria-hidden="true" focusable="false">' +
    '<path class="hod-spot__stem" pathLength="1" d="M7 3C16 16 17 34 12 53"/>' +
    '<path class="hod-spot__head" pathLength="1" d="M7.5 47.5L12 54L16.8 48.2"/></svg>';
  var ASPECT_L = 1.5, ASPECT_P = 2 / 3; // landscape fold files are 3:2, portrait 2:3 (work/fold-images/build.py)
  var shownOnce = false;

  function items() { return Array.prototype.slice.call(fold.querySelectorAll('.hod-fold__features > li')); }
  function focal() {
    var img = fold.querySelector('.hod-fold__picture.is-in img.hod-fold__img, .hod-fold__picture:not(.hod-fold__picture--incoming) img.hod-fold__img');
    var p = img ? getComputedStyle(img).objectPosition.split(' ') : [];
    var f = function (v) { v = parseFloat(v); return isNaN(v) ? 0.5 : v / 100; };
    return [f(p[0]), f(p[1])];
  }
  function overlap(a, b, pad) {
    return a.left < b.right + pad && a.right > b.left - pad && a.top < b.bottom + pad && a.bottom > b.top - pad;
  }
  function render() {
    // Fixer C (1 Oct): wait for assets/hod-fold-pdp.css (async). Until it applies, the layer would sit in the flow and
    // its unstyled arrows would push the copy (CLS); window load re-runs this once the sheet is in.
    if (getComputedStyle(layer).position !== 'absolute') return;
    layer.textContent = '';
    items().forEach(function (li) { li.classList.remove('is-spotted'); });
    fold.classList.remove('has-spots', 'has-spots-p');
    var portrait = !landscape.matches;
    layer.classList.toggle('hod-spots--p', portrait);
    var keys = (fold.getAttribute('data-hod-fold-key') || '').split('+');
    var spots = data[portrait ? keys[1] : keys[0]];
    var ASPECT = portrait ? ASPECT_P : ASPECT_L;
    // The critical CSS hides the text list from the first paint at these sizes; .hod-fold--spots-none brings it back
    // only when no arrow can be drawn (no spot data for this photo, or every label collides).
    if (!spots || (!portrait && !wide.matches) || fold.classList.contains('hod-fold--quiet')) {
      fold.classList.add('hod-fold--spots-none');
      return;
    }
    var lis = items();
    var box = fold.getBoundingClientRect();
    var W = box.width, H = box.height;
    var s = Math.max(W / ASPECT, H) ; // rendered photo height (cover)
    var rw = s * ASPECT, rh = s;
    var fp = focal();
    var ox = (W - rw) * fp[0], oy = (H - rh) * fp[1];
    var inner = fold.querySelector('.hod-fold__inner');
    var avoid = [];
    if (inner) { var r = inner.getBoundingClientRect(); avoid.push({ left: r.left - box.left, right: r.right - box.left, top: r.top - box.top, bottom: r.bottom - box.top }); }
    var cue = fold.querySelector('.hod-fold__cue');
    if (cue) { var c = cue.getBoundingClientRect(); avoid.push({ left: c.left - box.left, right: c.right - box.left, top: c.top - box.top, bottom: c.bottom - box.top }); }
    var headerH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--hod-header-h')) || 84;
    var n = 0;
    spots.forEach(function (sp) {
      var li = lis[sp[0] - 1];
      if (!li) return;
      // Round 9: after [n, x, y, side] an entry may list backup points [x, y, side] on the same part, tried in
      // order when the main point collides at this screen size (so a colour swap never drops a feature).
      var points = [[sp[1], sp[2], sp[3]]].concat(sp.slice(4));
      var el = document.createElement('div');
      el.className = 'hod-spot';
      el.style.transition = 'none'; // measure the final position, not a mid-transition one
      el.innerHTML = ARROW + ARROW_D + '<span class="hod-spot__label"></span>';
      el.querySelector('.hod-spot__label').textContent = li.textContent.replace(/\s+/g, ' ').trim();
      layer.appendChild(el);
      var tryside = function (side) {
        el.classList.toggle('hod-spot--d', side === 'd');
        el.classList.toggle('hod-spot--l', side.charAt(0) === 'l');
        el.classList.toggle('hod-spot--b', side.charAt(1) === 'b');
        var ar = el.querySelector(side === 'd' ? '.hod-spot__arrow--d' : '.hod-spot__arrow:not(.hod-spot__arrow--d)').getBoundingClientRect();
        var lb = el.querySelector('.hod-spot__label').getBoundingClientRect();
        var b = { left: Math.min(ar.left, lb.left) - box.left, right: Math.max(ar.right, lb.right) - box.left,
          top: Math.min(ar.top, lb.top) - box.top, bottom: Math.max(ar.bottom, lb.bottom) - box.top };
        var edge = W < 600 ? 14 : 24;
        if (b.left < edge || b.right > W - edge || b.top < headerH * 0.65 + 8 || b.bottom > H - 16) return null;
        for (var i = 0; i < avoid.length; i++) if (overlap(b, avoid[i], 10)) return null;
        return b;
      };
      // Preferred side first ('r' / 'l' = label up and to the right / left; 'rb' / 'lb' = below), then the mirror,
      // then the other height.
      var flip = function (x) { return x.charAt(0) + (x.charAt(1) === 'b' ? '' : 'b'); };
      var b = null;
      for (var k = 0; k < points.length && !b; k++) {
        el.style.left = (ox + points[k][0] * rw) + 'px';
        el.style.top = (oy + points[k][1] * rh) + 'px';
        var pref = points[k][2] || 'r', mirror = (pref.charAt(0) === 'l' ? 'r' : 'l') + pref.slice(1);
        b = pref === 'd' ? tryside('d') : (tryside(pref) || tryside(mirror) || tryside(flip(pref)) || tryside(flip(mirror)));
      }
      if (!b) { layer.removeChild(el); return; }
      avoid.push(b);
      el.style.transition = '';
      li.classList.add('is-spotted');
      el.style.setProperty('--hod-spot-i', n++);
    });
    fold.classList.toggle('hod-fold--spots-none', !n);
    if (n) {
      fold.classList.add(portrait ? 'has-spots-p' : 'has-spots');
      if (!shownOnce && !reduce.matches) {
        layer.classList.add('is-entering');
        window.setTimeout(function () { layer.classList.remove('is-entering'); }, 30);
      }
      shownOnce = true;
    }
  }
  var raf = 0;
  function schedule() { cancelAnimationFrame(raf); raf = requestAnimationFrame(render); }
  new MutationObserver(function () { window.setTimeout(schedule, 300); }).observe(fold, { attributes: true, attributeFilter: ['data-hod-fold-key'] });
  window.addEventListener('resize', schedule);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(schedule);
  window.addEventListener('load', schedule);
  schedule();
})();
